import React, { useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Select } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useFonts from 'packages/pages/editor/RightSideMenu/Shots/Text/Fonts/useFonts';
import useTextSelection from 'packages/hooks/useTextSelection';
import generateTestId from 'packages/helpers/testIdHelpers';
import type { Props } from './types';
import type { FontFamily as FontFamilyType } from '../types';

const FontFamily = ({ creativeModel, selectedElement, disabled }: Props) => {
    const { id, locked } = selectedElement;
    const { fontIdData, fontId: rawFontIds } = useTextSelection();
    const [, , mixedValue] = fontIdData;
    const [searchValue, setSearchValue] = useState('');
    const { loadDefaultFontByFamily, fontsList, dupliacateFonts } = useFonts();
    const { translate } = useTranslate();

    const [fontFamilyIds, familyName] = useMemo(() => {
        const fontFamilyIds = new Set();
        let familyName = '';

        Object.values(fontsList).forEach(({ id, name, fonts }) => {
            if (fonts.some((f) => rawFontIds.includes(String(f.id)))) {
                familyName = familyName || name;
                fontFamilyIds.add(id);
            } else {
                const duplicate = dupliacateFonts.find((f) => f.fonts.some((ff) => rawFontIds.includes(String(ff.id))));
                // Display 'Default' if no fonts match and no duplicates found
                familyName = duplicate ? duplicate.name : 'Default';
            }
        });

        // Comparing rawFontIds since some fonts may not be in the fontsList
        if (rawFontIds.length > 1) {
            familyName = mixedValue;
        }

        return [fontFamilyIds, familyName];
    }, [rawFontIds, fontsList, mixedValue]);

    const isFontMissing = () => {
        const allFontsListIds = Object.values(fontsList).flatMap((family) => family.fonts.map((f) => f.id.toString()));
        const allDuplicateFontIds = dupliacateFonts.flatMap((f) => f.fonts.map((ff) => ff.id.toString()));

        const diff = rawFontIds.filter((id) => !allFontsListIds.includes(id) && !allDuplicateFontIds.includes(id));

        return diff.length > 0;
    };

    const familyListArray = useMemo(() => {
        if (searchValue === '') {
            return Object.values(fontsList);
        }

        return Object.values(fontsList).filter((f) => f.name.toLowerCase().includes(searchValue.toLowerCase()));
    }, [fontsList, searchValue]);

    const handleChange = useCallback<(family: FontFamilyType) => Promise<void>>(
        async (family) => {
            const font = loadDefaultFontByFamily(family.fonts);
            const param = {
                updateTextSettingForSelection: {
                    settings: {
                        fontId: font.id.toString(),
                    },
                },
            };

            creativeModel.updateElement(id, param);
        },
        [creativeModel, id, loadDefaultFontByFamily],
    );

    return (
        <Form.Group>
            <Dropdown
                minWidth="288px"
                onClose={() => setSearchValue('')}
                {...generateTestId('shots_text_typeface')}
                trigger={({ isOpen, ...triggerProps }) => (
                    <Button
                        aria-label="shots_text_typeface"
                        isPressed={isOpen}
                        rightIcon={<Dropdown.Arrow />}
                        isDisabled={disabled || locked}
                        isFullWidth
                        {...triggerProps}
                    >
                        <Dropdown.SelectButtonText>{familyName}</Dropdown.SelectButtonText>
                    </Button>
                )}
            >
                <Select.SearchInput
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder={translate('editor.sidebar.shots.fonts.fontfamily.search')}
                    aria-label={translate('editor.sidebar.shots.fonts.fontfamily.search.label')}
                />
                {familyListArray.map((family) => (
                    <Dropdown.Item
                        key={family.id}
                        onClick={() => handleChange(family)}
                        isChecked={fontFamilyIds.has(family.id)}
                    >
                        {family.name}
                    </Dropdown.Item>
                ))}
            </Dropdown>
            {isFontMissing() && (
                <Form.HelperText icon={<Form.IconError />}>
                    {isFontMissing() ? translate('editor.sidebar.shots.fonts.fontfamily.missing') : ''}
                </Form.HelperText>
            )}
        </Form.Group>
    );
};

export default FontFamily;
